// The create form's half of the rewards file import (t-43).
//
// The board does not exist yet, so this dialog never talks to the server: the
// file is read in the browser and turned straight into the draft rows of
// section 5, which are created with the board like any other. That also means
// there is no plan — nothing to diff against — so what the file brought is
// simply listed, and the operator decides whether it is the right file.
//
// The dialog deliberately knows nothing about what a draft row IS. Whether a
// file row can become one is a question about tiers, ranges and payloads that
// the form already answers for the rows typed by hand, so the form hands that
// verdict in through `review` and gets its own rows back. That keeps
// validateRewardDraft the single description of a legal row rather than a rule
// this file would have to copy and then keep in step.

import { useCallback, useRef, useState, type ReactNode, type DragEvent } from "react";
import { File as FileIcon, Upload } from "lucide-react";

import type { RewardsFile } from "./types";
import {
  MAX_REWARDS_FILE_BYTES,
  REWARDS_FILE_TOO_BIG,
  parseRewardsFile,
  readRewardsFileText,
  rewardsFileMeta,
  type RewardsFileMeta,
} from "./rewardsFile";
import { Alert } from "../../components/Alert/Alert";
import { Button } from "../../components/Button/Button";
import { Modal } from "../../components/Modal/Modal";
import styles from "./ImportRewardsDraftModal.module.css";

/** One line of the "From the file" list. */
export interface RewardsFilePreviewRow {
  name: string;
  /**
   * The tier chip. Absent when the board isn't tiered and there is no such
   * column to show; `null` for a reward that pays out in every tier ("All
   * tiers"); otherwise the display name of the tier row it is bound to.
   */
  tier?: string | null;
  /** `rank 1 – 3`, `pct 0 – 100`, or `tier threshold`. */
  meta: string;
}

/**
 * One reason a file can't be taken. `reason` carries markup because the
 * mockup sets the tier key and the word `payload` as code — these problems are
 * built on the client, so there is no server string to print verbatim.
 */
export interface RewardsFileProblem {
  /** 1-based position in the file, as the operator counts rows in an editor. */
  index: number;
  name: string;
  reason: ReactNode;
}

/**
 * The form's verdict on a file. All or nothing on purpose: a row that can't be
 * shown in the editor can't be silently dropped or reshaped either, so one bad
 * row turns the whole file away and says why.
 */
export type RewardsFileReview<Row> =
  | { ok: true; rows: Row[]; preview: RewardsFilePreviewRow[] }
  | { ok: false; problems: RewardsFileProblem[] };

/** What the file does to the rows already in the draft. */
export type ImportRewardsMode = "append" | "replace";

interface Props<Row> {
  open: boolean;
  onClose: () => void;
  /** Rows the draft editor already holds — see .modeRow in the stylesheet. */
  draftCount: number;
  review: (file: RewardsFile) => RewardsFileReview<Row>;
  onApply: (rows: Row[], mode: ImportRewardsMode, fileName: string) => void;
}

/**
 * The picked file, once something is known about it. `meta` is null when the
 * file never parsed far enough to describe itself — the name is then all the
 * bar can honestly show.
 */
type Picked<Row> =
  | {
      state: "ok";
      fileName: string;
      meta: RewardsFileMeta;
      rows: Row[];
      preview: RewardsFilePreviewRow[];
    }
  | {
      state: "rejected";
      fileName: string;
      meta: RewardsFileMeta | null;
      problems: ReactNode[];
    };

export function ImportRewardsDraftModal<Row>({
  open,
  onClose,
  draftCount,
  review,
  onApply,
}: Props<Row>) {
  const [picked, setPicked] = useState<Picked<Row> | null>(null);
  const [mode, setMode] = useState<ImportRewardsMode>("append");
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const reset = useCallback(() => {
    setPicked(null);
    setMode("append");
    setDragOver(false);
  }, []);

  /**
   * Every way out of the dialog — close button, backdrop, Escape, Cancel.
   *
   * Memoised only so the handler keeps one identity for the dialog's life.
   * Modal no longer depends on it: it reaches onClose through a ref and keys
   * its focus effect on `open`, so a fresh closure per render would not move
   * the operator's focus.
   */
  const close = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  function openFilePicker() {
    fileRef.current?.click();
  }

  async function handleFile(file: globalThis.File | null | undefined) {
    if (!file) return;
    if (file.size > MAX_REWARDS_FILE_BYTES) {
      setPicked({
        state: "rejected",
        fileName: file.name,
        meta: null,
        problems: [REWARDS_FILE_TOO_BIG],
      });
      return;
    }
    let text: string;
    try {
      text = await readRewardsFileText(file);
    } catch {
      setPicked({
        state: "rejected",
        fileName: file.name,
        meta: null,
        problems: ["Couldn't read this file. Try picking it again."],
      });
      return;
    }
    // Two readings, in this order: what the file itself must be, and then
    // whether this board can hold it. The second one is the form's.
    const parsed = parseRewardsFile(text);
    if (!parsed.ok) {
      setPicked({
        state: "rejected",
        fileName: file.name,
        meta: null,
        problems: parsed.problems,
      });
      return;
    }
    const meta = rewardsFileMeta(parsed.file);
    const reviewed = review(parsed.file);
    if (!reviewed.ok) {
      setPicked({
        state: "rejected",
        fileName: file.name,
        meta,
        problems: reviewed.problems.map((p) => (
          <>
            reward {p.index} <b>{p.name.trim() || "(unnamed)"}</b> — {p.reason}
          </>
        )),
      });
      return;
    }
    setPicked({
      state: "ok",
      fileName: file.name,
      meta,
      rows: reviewed.rows,
      preview: reviewed.preview,
    });
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    void handleFile(e.dataTransfer?.files?.[0]);
  }

  const loaded = picked?.state === "ok" ? picked : null;
  const rejected = picked?.state === "rejected" ? picked : null;
  const count = loaded?.rows.length ?? 0;

  function apply() {
    if (!loaded || loaded.rows.length === 0) return;
    onApply(loaded.rows, mode, loaded.fileName);
    // The host closes the dialog; without this the next open would still be
    // showing the file that was just taken.
    reset();
  }

  const fileBar = picked && (
    <div
      className={
        rejected ? `${styles.fileBar} ${styles.fileBarSpaced}` : styles.fileBar
      }
    >
      <span className={styles.fileIcon} aria-hidden="true">
        <FileIcon size={16} />
      </span>
      <div className={styles.fileCol}>
        <div className={styles.fileName}>{picked.fileName}</div>
        {picked.meta && (
          <div className={styles.fileMeta}>
            {picked.meta.count}
            {picked.meta.boardName && (
              <>
                {" · from "}
                <b>{picked.meta.boardName}</b>
              </>
            )}
            {picked.meta.projectName && <>{` · ${picked.meta.projectName}`}</>}
            {picked.meta.exported && <>{` · ${picked.meta.exported}`}</>}
          </div>
        )}
      </div>
      <Button type="button" variant="ghost" size="sm" onClick={openFilePicker}>
        Choose another…
      </Button>
    </div>
  );

  const footer = (
    <>
      <Button type="button" variant="ghost" onClick={close}>
        Cancel
      </Button>
      {rejected ? (
        <Button type="button" variant="secondary" onClick={openFilePicker}>
          Choose another file
        </Button>
      ) : (
        <Button
          type="button"
          variant="primary"
          disabled={count === 0}
          onClick={apply}
        >
          {count === 0
            ? "Add rewards"
            : count === 1
              ? "Add 1 reward"
              : `Add ${count} rewards`}
        </Button>
      )}
    </>
  );

  return (
    <Modal
      open={open}
      onClose={close}
      title="Import rewards"
      width="sm"
      footer={footer}
    >
      {fileBar}

      {!picked && (
        <div
          className={
            dragOver ? `${styles.dropzone} ${styles.dropzoneOver}` : styles.dropzone
          }
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          <span className={styles.dzIcon} aria-hidden="true">
            <Upload size={20} />
          </span>
          <span className={styles.dzTitle}>Drop a rewards file here</span>
          <span className={styles.dzOr}>or</span>
          <Button type="button" variant="secondary" size="sm" onClick={openFilePicker}>
            Choose file
          </Button>
          <span className={styles.dzHint}>.json · up to 1 MB</span>
        </div>
      )}

      {loaded && (
        <>
          {draftCount > 0 && (
            <div className={styles.modeRow}>
              <div className={styles.seg} role="radiogroup" aria-label="Import mode">
                {(["append", "replace"] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    role="radio"
                    aria-checked={mode === m}
                    className={
                      mode === m
                        ? `${styles.segOpt} ${styles.segOptActive}`
                        : styles.segOpt
                    }
                    onClick={() => setMode(m)}
                  >
                    {m === "append" ? "Append" : "Replace"}
                  </button>
                ))}
              </div>
              <p className={styles.modeHint}>
                {draftCount === 1
                  ? `Your 1 draft row is ${mode === "append" ? "kept" : "replaced"}.`
                  : `Your ${draftCount} draft rows are ${
                      mode === "append" ? "kept" : "replaced"
                    }.`}
              </p>
            </div>
          )}

          <div className={styles.previewList}>
            <div className={styles.previewHead}>From the file</div>
            {loaded.preview.length === 0 ? (
              <div className={styles.previewEmpty}>
                This file carries no rewards.
              </div>
            ) : (
              loaded.preview.map((row, i) => (
                <div key={i} className={styles.previewRow}>
                  <span className={styles.pName}>{row.name}</span>
                  {row.tier !== undefined && (
                    <span
                      className={
                        row.tier === null
                          ? `${styles.tierChip} ${styles.tierChipAll}`
                          : styles.tierChip
                      }
                    >
                      {row.tier ?? "All tiers"}
                    </span>
                  )}
                  <span className={styles.pMeta}>{row.meta}</span>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {rejected && (
        <Alert
          variant="danger"
          title="This file doesn’t fit the board you are configuring"
        >
          <ul className={styles.problems}>
            {rejected.problems.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
          Fix the tiers on step 4, or edit the file, and import again.
        </Alert>
      )}

      {/* Last in the body so the initial focus lands on a button the operator
         can see — Modal focuses the first focusable it finds there. */}
      <input
        ref={fileRef}
        type="file"
        accept="application/json,.json"
        className={styles.fileInput}
        aria-label="Rewards file"
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Reset so picking the same file twice fires change again.
          e.target.value = "";
          void handleFile(file);
        }}
      />
    </Modal>
  );
}
